import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowRight,
    CheckCircle2,
    X,
} from "lucide-react";

/**
 * Generic confirm modal — replaces ugly `window.confirm` for irreversible
 * status changes (Programmer immersion, Refuser, Onboarding, etc.).
 *
 * @param {Object} props
 * @param {string} props.title          Title of the confirmation
 * @param {string|JSX.Element} props.body Body text (or JSX)
 * @param {string} props.confirmLabel   CTA button label (defaults to "Confirmer")
 * @param {string} [props.cancelLabel]  Cancel button label (defaults to "Annuler")
 * @param {"orange"|"red"|"green"|"slate"} [props.tone] Visual tone of the CTA
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 * @param {string} [props.testid]       Optional data-testid for the modal root
 */
const TONE_CLASSES = {
    orange: {
        icon: "bg-shariti-orange-soft text-shariti-orange",
        btn: "bg-shariti-orange text-white hover:bg-[#d66528]",
    },
    red: {
        icon: "bg-red-50 text-shariti-red",
        btn: "bg-shariti-red text-white hover:bg-[#c64545]",
    },
    green: {
        icon: "bg-emerald-50 text-emerald-600",
        btn: "bg-emerald-600 text-white hover:bg-emerald-700",
    },
    slate: {
        icon: "bg-shariti-cream text-slate-600",
        btn: "bg-shariti-black text-white hover:bg-slate-800",
    },
};

export default function ConfirmModal({
    title,
    body,
    confirmLabel = "Confirmer",
    cancelLabel = "Annuler",
    tone = "orange",
    icon: IconComp = CheckCircle2,
    onConfirm,
    onCancel,
    onSecondary,          // optional 3rd button (e.g. « Ou plutôt vivier »)
    secondaryLabel,
    testid = "confirm-modal",
}) {
    const tc = TONE_CLASSES[tone] || TONE_CLASSES.orange;
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[60] bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid={testid}
            onClick={onCancel}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-7 max-w-md w-full"
            >
                <div className="flex items-start gap-3">
                    <div className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${tc.icon}`}>
                        <IconComp className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-black tracking-tight text-shariti-black">{title}</h2>
                        <div className="mt-1.5 text-sm font-medium text-slate-600 leading-relaxed">
                            {body}
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        data-testid={`${testid}-close`}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-6 flex justify-end gap-2 flex-wrap">
                    <button
                        onClick={onCancel}
                        data-testid={`${testid}-cancel`}
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition"
                    >
                        {cancelLabel}
                    </button>
                    {onSecondary && (
                        <button
                            onClick={onSecondary}
                            data-testid={`${testid}-secondary`}
                            className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 px-5 h-10 font-bold text-xs transition"
                        >
                            {secondaryLabel || "Autre action"}
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        data-testid={`${testid}-confirm`}
                        className={`inline-flex items-center gap-2 rounded-full px-5 h-10 font-bold text-xs transition shadow-btn ${tc.btn}`}
                    >
                        {confirmLabel}
                        <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

// Re-export AlertTriangle for caller convenience when picking icons
export { AlertTriangle, CheckCircle2 };
